import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../ui';
import { createAuditedClient } from '../utils/auditClient';
import { getUrl } from 'aws-amplify/storage';
import {
  cardStyle,
  labelStyle,
  inputStyle,
  textareaStyle,
  sectionTitleStyle,
  twoColGrid,
  threeColGrid,
  COLORS,
  helperTextStyle,
} from './handoverStyles';
import {
  dataUrlToFile,
  fuelStringToPercent,
  uploadFiles,
  uploadedItemsToKeys,
  countPendingUploads,
  countFailedUploads,
} from './handoverUtils';
import { getSubstitutionContractText } from './handoverContracts';
import { SubmitHandoverForm } from './handoverQueries';
import SignaturePad from '../components/handover/SignaturePad';
import FileUploadWithPreview from '../components/handover/FileUploadWithPreview';
import {
  getReservationCurrentCarSegment,
  reservationUsesCarInWindow,
} from '../utils/reservationCarSegments';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { format } from 'date-fns';

const client = createAuditedClient('SubstitutionHandoverForm');
const CURRENCY_OPTIONS = ['USD', 'EUR', 'GEL', 'LEK', 'GBP', 'AED'];

const ByCompanyByStart = /* GraphQL */ `
  query ByCompanyByStart($CompanyId: String!, $start: ModelStringKeyConditionInput) {
    reservationsByCompanyByStart(CompanyId: $CompanyId, start: $start, limit: 1000) {
      items {
        id
        CompanyId
        carId
        carSubstitutions
        start
        end
        approvalStatus
        reservationRef
        customerName
      }
      nextToken
    }
  }
`;

const MaintByCompanyByStart = /* GraphQL */ `
  query MaintByCompanyByStart($CompanyId: String!, $start: ModelStringKeyConditionInput) {
    maintenanceByCompanyByStart(CompanyId: $CompanyId, start: $start, limit: 1000) {
      items {
        id
        CompanyId
        carId
        start
        end
        approvalStatus
        title
      }
      nextToken
    }
  }
`;

function companyTzForLocation(location) {
  if (String(location).toUpperCase() === 'AL') return 'Europe/Tirane';
  if (String(location).toUpperCase() === 'GE') return 'Asia/Tbilisi';
  return 'UTC';
}

function currencyForLocation(location) {
  return String(location).toUpperCase() === 'AL' ? 'EUR' : 'USD';
}

function safeStr(value) {
  if (value == null) return '';
  return String(value).trim();
}

function splitCompanyDateTime(utcIso, tz) {
  if (!utcIso) return { date: '', time: '' };
  const d = new Date(utcIso);
  if (Number.isNaN(d.getTime())) return { date: '', time: '' };
  const zoned = toZonedTime(d, tz);
  return {
    date: format(zoned, 'yyyy-MM-dd'),
    time: format(zoned, 'HH:mm'),
  };
}

function toUtcIsoFromCompanyParts(dateYmd, timeHm, tz) {
  if (!dateYmd || !timeHm) return '';
  const d = fromZonedTime(`${dateYmd}T${timeHm}:00`, tz);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

function overlapKey(endUtcIso) {
  if (!endUtcIso) return undefined;
  const end = new Date(endUtcIso);
  if (Number.isNaN(end.getTime())) return undefined;
  end.setUTCDate(end.getUTCDate() - 31);
  return { between: [end.toISOString(), endUtcIso] };
}

function isBlocking(row) {
  const approval = String(row?.approvalStatus || 'APPROVED').toUpperCase();
  return approval === 'APPROVED' || approval === 'PENDING';
}

function safeParseJson(value) {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(String(value));
  } catch {
    return {};
  }
}

function percentToFuelString(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  if (n <= 25) return '1/4';
  if (n <= 50) return '1/2';
  if (n <= 75) return '3/4';
  return 'full';
}

function deriveNameParts(fullName) {
  const clean = safeStr(fullName);
  if (!clean) return { firstName: '', lastName: '' };
  const parts = clean.split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' '),
  };
}

function buildDefaultEffectiveIso(reservation) {
  const now = new Date();
  const start = reservation?.start ? new Date(reservation.start) : null;
  const end = reservation?.end ? new Date(reservation.end) : null;
  let effective = now;

  if (start && !Number.isNaN(start.getTime()) && effective < start) effective = start;
  if (end && !Number.isNaN(end.getTime()) && effective >= end) {
    effective = new Date(end.getTime() - 60 * 1000);
  }

  return Number.isNaN(effective.getTime()) ? new Date().toISOString() : effective.toISOString();
}

function normalizeCurrency(value, fallback) {
  const currency = safeStr(value).toUpperCase();
  return currency || fallback;
}

function parsePositiveAmount(value) {
  const raw = safeStr(value);
  if (!raw) return null;
  const amount = Number(raw);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  return Number(amount.toFixed(2));
}

function buildSubstitutionCharge(form, fallbackCurrency) {
  const statusRaw = safeStr(form.extraChargeStatus).toLowerCase();
  const status = ['free', 'unpaid', 'paid'].includes(statusRaw) ? statusRaw : 'free';
  const description = safeStr(form.extraChargeDescription);
  const paymentNote = safeStr(form.extraChargeNote);
  const currency = normalizeCurrency(form.extraChargeCurrency, fallbackCurrency);
  const amount = parsePositiveAmount(form.extraChargeAmount);

  return {
    status,
    description,
    amount: status === 'free' ? null : amount,
    currency,
    paymentMethod: status === 'paid' ? safeStr(form.extraChargePaymentMethod) : '',
    paymentNote,
  };
}

export default function SubstitutionHandoverForm({
  reservationId,
  reservation,
  cars,
  claims,
  location,
  pickupHandover = null,
  existingHandover = null,
  readOnly = false,
  pushAlert,
}) {
  const companyTz = useMemo(() => companyTzForLocation(location), [location]);
  const defaultCurrency = useMemo(() => currencyForLocation(location), [location]);
  const existingEquipment = useMemo(
    () => safeParseJson(existingHandover?.equipmentNotes),
    [existingHandover]
  );
  const hasExistingHandover = !!existingHandover;

  const currentSegment = useMemo(
    () => getReservationCurrentCarSegment(reservation),
    [reservation]
  );
  const currentCarId = hasExistingHandover
    ? safeStr(existingEquipment.fromCarId) ||
      safeStr(currentSegment?.transition?.fromCarId) ||
      safeStr(reservation?.originalCarId) ||
      safeStr(reservation?.carId)
    : currentSegment?.carId || reservation?.carId || '';
  const currentCar = useMemo(
    () => (cars || []).find((car) => car.id === currentCarId) || null,
    [cars, currentCarId]
  );
  const existingReplacementCarId =
    safeStr(existingEquipment.toCarId) || safeStr(existingHandover?.carId);
  const existingReplacementLabel = [
    safeStr(existingEquipment.toCarName || existingHandover?.carName || existingReplacementCarId),
    safeStr(existingEquipment.toCarPlate || existingHandover?.carPlate),
  ]
    .filter(Boolean)
    .join(' ');
  const replacementCars = useMemo(
    () =>
      (cars || [])
        .filter((car) => car.active && car.CompanyId === reservation?.CompanyId && car.id !== currentCarId)
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))),
    [cars, reservation?.CompanyId, currentCarId]
  );

  const pickupDetails = useMemo(() => {
    const parsed = safeParseJson(pickupHandover?.equipmentNotes);
    const fromReservation = deriveNameParts(reservation?.customerName || '');
    const fromPickup = deriveNameParts(pickupHandover?.customerName || reservation?.customerName || '');
    return {
      firstName: fromPickup.firstName || fromReservation.firstName,
      lastName: fromPickup.lastName || fromReservation.lastName,
      email:
        pickupHandover?.customerEmail ||
        safeStr(parsed.lesseeEmail) ||
        safeStr(reservation?.customerEmail),
      phone:
        pickupHandover?.customerPhone ||
        safeStr(parsed.lesseePhone) ||
        safeStr(reservation?.customerPhone),
      idNumber: safeStr(parsed.idNumber),
      drivingLicenseNumber:
        pickupHandover?.licenseNumber || safeStr(parsed.drivingLicenseNumber),
      drivingLicenseCountry: safeStr(parsed.drivingLicenseCountry),
    };
  }, [pickupHandover, reservation]);

  const defaultEffectiveIso = useMemo(() => buildDefaultEffectiveIso(reservation), [reservation]);
  const defaultEffectiveParts = useMemo(
    () => splitCompanyDateTime(defaultEffectiveIso, companyTz),
    [defaultEffectiveIso, companyTz]
  );
  const reservationStartParts = useMemo(
    () => splitCompanyDateTime(reservation?.start, companyTz),
    [reservation?.start, companyTz]
  );
  const reservationEndParts = useMemo(
    () => splitCompanyDateTime(reservation?.end, companyTz),
    [reservation?.end, companyTz]
  );

  const [banner, setBanner] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showContract, setShowContract] = useState(false);
  const [availability, setAvailability] = useState({ checked: false, ok: null, message: '' });
  const [odoFuelUploads, setOdoFuelUploads] = useState([]);
  const [bodyPhotoUploads, setBodyPhotoUploads] = useState([]);
  const [existingOdoFuelUrls, setExistingOdoFuelUrls] = useState([]);
  const [existingBodyPhotoUrls, setExistingBodyPhotoUrls] = useState([]);
  const [initializedExistingId, setInitializedExistingId] = useState(null);
  const [form, setForm] = useState({
    substitutionDate: defaultEffectiveParts.date,
    substitutionTime: defaultEffectiveParts.time,
    substitutionLocation: reservation?.pickupLocation || '',
    fromCarId: currentCarId,
    toCarId: '',
    reason: '',
    currentFuel: '',
    odometer: '',
    extraChargeStatus: 'free',
    extraChargeAmount: '',
    extraChargeCurrency: defaultCurrency,
    extraChargePaymentMethod: '',
    extraChargeDescription: '',
    extraChargeNote: '',
    lesseeFirstName: pickupDetails.firstName,
    lesseeLastName: pickupDetails.lastName,
    lesseeEmail: pickupDetails.email,
    lesseeEmailConfirm: pickupDetails.email,
    lesseePhone: pickupDetails.phone,
    idNumber: pickupDetails.idNumber,
    drivingLicenseNumber: pickupDetails.drivingLicenseNumber,
    drivingLicenseCountry: pickupDetails.drivingLicenseCountry,
    extraNotes: '',
    lesseeSignatureDataUrl: '',
  });

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      fromCarId: currentCarId,
      substitutionDate: prev.substitutionDate || defaultEffectiveParts.date,
      substitutionTime: prev.substitutionTime || defaultEffectiveParts.time,
    }));
  }, [currentCarId, defaultEffectiveParts.date, defaultEffectiveParts.time]);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      extraChargeCurrency: prev.extraChargeCurrency || defaultCurrency,
    }));
  }, [defaultCurrency]);

  useEffect(() => {
    if (!existingHandover || initializedExistingId === existingHandover.id) return;

    const eq = existingEquipment || {};
    const effectiveParts = splitCompanyDateTime(
      eq.effectiveAt || existingHandover.signedAt || existingHandover.createdAt,
      companyTz
    );
    const charge =
      eq.substitutionCharge && typeof eq.substitutionCharge === 'object'
        ? eq.substitutionCharge
        : {};
    const nameParts = deriveNameParts(existingHandover.customerName || '');

    setForm((prev) => ({
      ...prev,
      ...eq,
      substitutionDate: effectiveParts.date || eq.substitutionDate || prev.substitutionDate,
      substitutionTime: effectiveParts.time || eq.substitutionTime || prev.substitutionTime,
      substitutionLocation: safeStr(eq.substitutionLocation) || prev.substitutionLocation,
      fromCarId: safeStr(eq.fromCarId) || currentCarId || prev.fromCarId,
      toCarId: safeStr(eq.toCarId) || safeStr(existingHandover.carId) || prev.toCarId,
      reason: safeStr(eq.reason || existingHandover.damageNotes) || prev.reason,
      currentFuel:
        safeStr(eq.currentFuel) ||
        percentToFuelString(existingHandover.fuelLevelPercent) ||
        prev.currentFuel,
      odometer:
        existingHandover.odometerKm != null
          ? String(existingHandover.odometerKm)
          : safeStr(eq.odometer) || prev.odometer,
      extraChargeStatus: safeStr(charge.status || eq.extraChargeStatus) || prev.extraChargeStatus,
      extraChargeAmount:
        charge.amount != null ? String(charge.amount) : safeStr(eq.extraChargeAmount),
      extraChargeCurrency:
        safeStr(charge.currency || eq.extraChargeCurrency) || defaultCurrency,
      extraChargePaymentMethod:
        safeStr(charge.paymentMethod || eq.extraChargePaymentMethod) || '',
      extraChargeDescription:
        safeStr(charge.description || eq.extraChargeDescription) || '',
      extraChargeNote: safeStr(charge.paymentNote || eq.extraChargeNote) || '',
      lesseeFirstName:
        safeStr(eq.lesseeFirstName) || nameParts.firstName || prev.lesseeFirstName,
      lesseeLastName:
        safeStr(eq.lesseeLastName) || nameParts.lastName || prev.lesseeLastName,
      lesseeEmail:
        safeStr(eq.lesseeEmail || existingHandover.customerEmail) || prev.lesseeEmail,
      lesseeEmailConfirm:
        safeStr(eq.lesseeEmailConfirm || eq.lesseeEmail || existingHandover.customerEmail) ||
        prev.lesseeEmailConfirm,
      lesseePhone:
        safeStr(eq.lesseePhone || existingHandover.customerPhone) || prev.lesseePhone,
      idNumber: safeStr(eq.idNumber) || prev.idNumber,
      drivingLicenseNumber:
        safeStr(eq.drivingLicenseNumber || existingHandover.licenseNumber) ||
        prev.drivingLicenseNumber,
      drivingLicenseCountry:
        safeStr(eq.drivingLicenseCountry) || prev.drivingLicenseCountry,
      extraNotes: safeStr(eq.extraNotes) || prev.extraNotes,
      lesseeSignatureDataUrl: '',
    }));

    setSubmitted(true);
    setInitializedExistingId(existingHandover.id);
  }, [
    existingHandover,
    existingEquipment,
    initializedExistingId,
    companyTz,
    currentCarId,
    defaultCurrency,
  ]);

  useEffect(() => {
    if (existingHandover || !initializedExistingId) return;

    setInitializedExistingId(null);
    setSubmitted(false);
    setForm((prev) => ({
      ...prev,
      substitutionDate: defaultEffectiveParts.date,
      substitutionTime: defaultEffectiveParts.time,
      substitutionLocation: reservation?.pickupLocation || '',
      fromCarId: currentCarId,
      toCarId: '',
      reason: '',
      currentFuel: '',
      odometer: '',
      extraChargeStatus: 'free',
      extraChargeAmount: '',
      extraChargeCurrency: defaultCurrency,
      extraChargePaymentMethod: '',
      extraChargeDescription: '',
      extraChargeNote: '',
      lesseeFirstName: pickupDetails.firstName,
      lesseeLastName: pickupDetails.lastName,
      lesseeEmail: pickupDetails.email,
      lesseeEmailConfirm: pickupDetails.email,
      lesseePhone: pickupDetails.phone,
      idNumber: pickupDetails.idNumber,
      drivingLicenseNumber: pickupDetails.drivingLicenseNumber,
      drivingLicenseCountry: pickupDetails.drivingLicenseCountry,
      extraNotes: '',
      lesseeSignatureDataUrl: '',
    }));
  }, [
    existingHandover,
    initializedExistingId,
    defaultEffectiveParts.date,
    defaultEffectiveParts.time,
    reservation?.pickupLocation,
    currentCarId,
    defaultCurrency,
    pickupDetails,
  ]);

  useEffect(() => {
    if (!existingHandover) {
      setExistingOdoFuelUrls([]);
      setExistingBodyPhotoUrls([]);
      return undefined;
    }

    let cancelled = false;
    const carKeys = Array.isArray(existingHandover.carPhotoKeys)
      ? existingHandover.carPhotoKeys
      : [];
    const damageKeys = Array.isArray(existingHandover.damagePhotoKeys)
      ? existingHandover.damagePhotoKeys
      : [];

    const loadUrls = async (keys) => {
      const results = await Promise.all(
        keys.map(async (key) => {
          try {
            const { url } = await getUrl({ key });
            return { key, url: url.toString() };
          } catch (err) {
            console.warn('Failed to get URL for substitution photo key', key, err);
            return null;
          }
        })
      );
      return results.filter(Boolean);
    };

    (async () => {
      const [odoFuelUrls, bodyUrls] = await Promise.all([
        loadUrls(carKeys),
        loadUrls(damageKeys),
      ]);
      if (!cancelled) {
        setExistingOdoFuelUrls(odoFuelUrls);
        setExistingBodyPhotoUrls(bodyUrls);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [existingHandover]);

  const contractText = useMemo(() => getSubstitutionContractText(location), [location]);
  const pendingPhotoUploads =
    countPendingUploads(odoFuelUploads) + countPendingUploads(bodyPhotoUploads);
  const failedPhotoUploads =
    countFailedUploads(odoFuelUploads) + countFailedUploads(bodyPhotoUploads);
  const effectiveAtIso = useMemo(
    () => toUtcIsoFromCompanyParts(form.substitutionDate, form.substitutionTime, companyTz),
    [form.substitutionDate, form.substitutionTime, companyTz]
  );
  const replacementCar = useMemo(
    () => (cars || []).find((car) => car.id === form.toCarId) || null,
    [cars, form.toCarId]
  );

  const effectiveWindowValid =
    !!effectiveAtIso &&
    !!reservation?.end &&
    effectiveAtIso > (reservation?.start || '') &&
    effectiveAtIso < reservation.end;

  useEffect(() => {
    setAvailability({ checked: false, ok: null, message: '' });
  }, [form.toCarId, effectiveAtIso, reservation?.end]);

  function showAlert(type, title, message) {
    setBanner({ type, title, message });
    pushAlert?.({ type, title, message });
  }

  async function checkAvailability(selectedCarId = form.toCarId, selectedEffectiveIso = effectiveAtIso) {
    if (!reservation?.CompanyId || !selectedCarId || !selectedEffectiveIso || !reservation?.end) {
      const next = { checked: false, ok: null, message: '' };
      setAvailability(next);
      return next;
    }

    const startCond = overlapKey(reservation.end);
    const [res, mt] = await Promise.all([
      client.graphql({
        query: ByCompanyByStart,
        variables: { CompanyId: reservation.CompanyId, start: startCond },
        authMode: 'userPool',
      }),
      client.graphql({
        query: MaintByCompanyByStart,
        variables: { CompanyId: reservation.CompanyId, start: startCond },
        authMode: 'userPool',
      }),
    ]);

    const reservations = (res?.data?.reservationsByCompanyByStart?.items || [])
      .filter((row) => row && row.id !== reservationId && isBlocking(row));
    const maintenance = (mt?.data?.maintenanceByCompanyByStart?.items || [])
      .filter((row) => row && isBlocking(row));

    const reservationHits = reservations.filter((row) =>
      reservationUsesCarInWindow(row, selectedCarId, selectedEffectiveIso, reservation.end)
    );
    const maintenanceHits = maintenance.filter(
      (row) =>
        row.carId === selectedCarId &&
        row.start &&
        row.end &&
        selectedEffectiveIso < row.end &&
        row.start < reservation.end
    );

    const next =
      !reservationHits.length && !maintenanceHits.length
        ? {
            checked: true,
            ok: true,
            message: 'Replacement car is free for the rest of this reservation.',
          }
        : {
            checked: true,
            ok: false,
            message: `Replacement car conflicts with ${reservationHits.length + maintenanceHits.length} existing item(s).`,
          };

    setAvailability(next);
    return next;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (submitting) return;

    if (readOnly) {
      showAlert('error', 'Contract locked', 'This substitution contract is read-only.');
      return;
    }
    if (!effectiveWindowValid) {
      showAlert('error', 'Invalid substitution time', 'Choose an effective date/time inside the active reservation window.');
      return;
    }
    if (!form.toCarId || form.toCarId === currentCarId) {
      showAlert('error', 'Replacement car required', 'Choose a different car for the substitution.');
      return;
    }
    if (!safeStr(form.reason)) {
      showAlert('error', 'Reason required', 'Add the reason for the substitution.');
      return;
    }
    if (!form.lesseeSignatureDataUrl) {
      showAlert('error', 'Signature required', 'Customer signature is required before saving.');
      return;
    }
    if (safeStr(form.lesseeEmail) !== safeStr(form.lesseeEmailConfirm)) {
      showAlert('error', 'Email mismatch', 'Lessee email and confirmation do not match.');
      return;
    }

    const substitutionCharge = buildSubstitutionCharge(form, defaultCurrency);
    if (substitutionCharge.status !== 'free' && !substitutionCharge.amount) {
      showAlert('error', 'Charge amount required', 'Enter a positive charge amount or mark the substitution as free.');
      return;
    }
    if (substitutionCharge.status === 'paid' && !substitutionCharge.paymentMethod) {
      showAlert('error', 'Payment method required', 'Choose how the substitution charge was paid.');
      return;
    }

    if (pendingPhotoUploads > 0) {
      showAlert(
        'error',
        'Uploads still running',
        `Wait for ${pendingPhotoUploads} photo upload${pendingPhotoUploads === 1 ? '' : 's'} to finish before saving.`
      );
      return;
    }

    if (failedPhotoUploads > 0) {
      showAlert(
        'error',
        'Photo uploads failed',
        `Remove or re-upload the ${failedPhotoUploads} failed photo${failedPhotoUploads === 1 ? '' : 's'} before saving.`
      );
      return;
    }

    try {
      setSubmitting(true);
      const availabilityResult = await checkAvailability();
      if (availabilityResult.ok === false) {
        showAlert('error', 'Replacement car unavailable', availabilityResult.message);
        return;
      }

      const carPhotoKeys = uploadedItemsToKeys(odoFuelUploads);
      const damagePhotoKeys = uploadedItemsToKeys(bodyPhotoUploads);

      let signatureKey = null;
      const signatureFile = dataUrlToFile(
        form.lesseeSignatureDataUrl,
        `lessee_signature_substitution_${reservationId}.png`
      );
      if (signatureFile) {
        const signatureKeys = await uploadFiles('handover/substitution/signature', reservationId, [signatureFile]);
        signatureKey = signatureKeys[0] || null;
      }

      const customerName = `${safeStr(form.lesseeFirstName)} ${safeStr(form.lesseeLastName)}`
        .trim()
        .replace(/\s+/g, ' ');

      const chargePayments =
        substitutionCharge.status === 'paid' && substitutionCharge.amount
          ? [{
              method: substitutionCharge.paymentMethod,
              amount: substitutionCharge.amount,
              currency: substitutionCharge.currency,
              note: substitutionCharge.paymentNote || substitutionCharge.description || '',
            }]
          : [];

      const equipmentNotes = {
        ...form,
        effectiveAt: effectiveAtIso,
        reason: safeStr(form.reason),
        fromCarId: currentCarId || null,
        fromCarName: currentCar?.name || currentSegment?.carName || null,
        fromCarPlate: currentCar?.plate || currentSegment?.carPlate || null,
        toCarId: replacementCar?.id || null,
        toCarName: replacementCar?.name || null,
        toCarPlate: replacementCar?.plate || null,
        reservationStart: reservation?.start || null,
        reservationEnd: reservation?.end || null,
        substitutionCharge,
        chargePayments,
        photoSummary: {
          odometerFuelCount: carPhotoKeys.length,
          bodyCount: damagePhotoKeys.length,
        },
      };
      delete equipmentNotes.lesseeSignatureDataUrl;

      const result = await client.graphql({
        query: SubmitHandoverForm,
        variables: {
          input: {
            CompanyId: reservation.CompanyId,
            reservationId,
            type: 'SUBSTITUTION',
            filledBySub: claims?.sub || null,
            filledByEmail: claims?.email || null,
            customerName: customerName || reservation?.customerName || null,
            customerEmail: safeStr(form.lesseeEmail) || null,
            customerPhone: safeStr(form.lesseePhone) || null,
            licenseNumber: safeStr(form.drivingLicenseNumber) || safeStr(form.idNumber) || null,
            carId: replacementCar?.id || null,
            carName: replacementCar?.name || null,
            carPlate: replacementCar?.plate || null,
            odometerKm: form.odometer ? parseInt(form.odometer, 10) : null,
            fuelLevelPercent: fuelStringToPercent(form.currentFuel),
            exteriorCondition: null,
            interiorCondition: null,
            damageNotes: safeStr(form.reason) || null,
            equipmentNotes: JSON.stringify(equipmentNotes),
            carPhotoKeys,
            damagePhotoKeys,
            signatureKey,
            ipAddress: null,
            signedAt: new Date().toISOString(),
          },
        },
        authMode: 'userPool',
      });

      if (!result?.data?.submitHandoverForm) throw new Error('Mutation returned false');

      setSubmitted(true);
      showAlert('success', 'Substitution saved', 'The replacement contract was saved, the calendar was adjusted, and emails were queued.');
    } catch (error) {
      console.error('Substitution handover submit failed', error);
      showAlert('error', 'Save failed', 'Failed to submit the substitution contract. Check console for details.');
    } finally {
      setSubmitting(false);
    }
  }

  const chargeStatus = form.extraChargeStatus;
  const replacementOptionExists = replacementCars.some((car) => car.id === form.toCarId);

  const renderImageGrid = (items, emptyText) => {
    if (!items || items.length === 0) {
      return (
        <div style={{ fontSize: 12, color: COLORS.gray[500], marginTop: 4, lineHeight: 1.5 }}>
          {emptyText}
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 6 }}>
        {items.map((p) => (
          <a
            key={p.key}
            href={p.url}
            target="_blank"
            rel="noreferrer"
            style={{
              display: 'inline-block',
              borderRadius: 10,
              overflow: 'hidden',
              border: `1px solid ${COLORS.gray[200]}`,
            }}
          >
            <img
              src={p.url}
              alt=""
              style={{ width: 100, height: 75, objectFit: 'cover', display: 'block' }}
            />
          </a>
        ))}
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 16 }}>
      {banner && (
        <div
          style={{
            borderRadius: 12,
            padding: '12px 14px',
            border: `1px solid ${
              banner.type === 'error'
                ? COLORS.danger
                : banner.type === 'success'
                ? COLORS.success
                : COLORS.primary
            }`,
            background:
              banner.type === 'error'
                ? COLORS.dangerLight
                : banner.type === 'success'
                ? COLORS.successLight
                : COLORS.primaryLight,
            color:
              banner.type === 'error'
                ? '#7f1d1d'
                : banner.type === 'success'
                ? '#065f46'
                : COLORS.primaryDark,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 4 }}>{banner.title}</div>
          <div style={{ fontSize: 13 }}>{banner.message}</div>
        </div>
      )}

      <section style={cardStyle()}>
        <h3 style={sectionTitleStyle}>Substitution summary</h3>
        <div style={twoColGrid}>
          <div>
            <div style={labelStyle}>Reservation</div>
            <div>{reservation?.reservationRef || reservationId}</div>
            <div style={helperTextStyle}>
              {reservationStartParts.date} {reservationStartParts.time} to {reservationEndParts.date}{' '}
              {reservationEndParts.time}
            </div>
          </div>
          <div>
            <div style={labelStyle}>Current assigned car</div>
            <div>
              {currentCar?.name || currentSegment?.carName || '-'}
              {currentCar?.plate
                ? ` (${currentCar.plate})`
                : currentSegment?.carPlate
                ? ` (${currentSegment.carPlate})`
                : ''}
            </div>
            <div style={helperTextStyle}>
              This car stays blocked until the substitution effective time.
            </div>
          </div>
        </div>
      </section>

      <section style={cardStyle()}>
        <h3 style={sectionTitleStyle}>Contract details</h3>
        <div style={twoColGrid}>
          <label>
            <span style={labelStyle}>Effective date</span>
            <input
              type="date"
              style={inputStyle}
              value={form.substitutionDate}
              onChange={(e) => setForm((prev) => ({ ...prev, substitutionDate: e.target.value }))}
              disabled={readOnly || submitted}
            />
          </label>

          <label>
            <span style={labelStyle}>Effective time</span>
            <input
              type="time"
              style={inputStyle}
              value={form.substitutionTime}
              onChange={(e) => setForm((prev) => ({ ...prev, substitutionTime: e.target.value }))}
              disabled={readOnly || submitted}
            />
          </label>
        </div>

        <div style={{ marginTop: 12 }}>
          <label>
            <span style={labelStyle}>Substitution location</span>
            <input
              style={inputStyle}
              value={form.substitutionLocation}
              onChange={(e) => setForm((prev) => ({ ...prev, substitutionLocation: e.target.value }))}
              disabled={readOnly || submitted}
              placeholder="Where the customer received the replacement car"
            />
          </label>
        </div>

        <div style={{ ...twoColGrid, marginTop: 12 }}>
          <label>
            <span style={labelStyle}>From car</span>
            <input
              style={{ ...inputStyle, background: COLORS.gray[50] }}
              readOnly
              value={
                currentCar
                  ? `${currentCar.name}${currentCar.plate ? ` (${currentCar.plate})` : ''}`
                  : currentSegment?.carName || currentCarId || ''
              }
            />
          </label>

          <label>
            <span style={labelStyle}>Replacement car</span>
            <select
              style={inputStyle}
              value={form.toCarId}
              onChange={(e) => setForm((prev) => ({ ...prev, toCarId: e.target.value }))}
              disabled={readOnly || submitted}
            >
              <option value="">Select replacement car</option>
              {form.toCarId && !replacementOptionExists && (
                <option value={form.toCarId}>
                  {existingReplacementLabel || form.toCarId}
                </option>
              )}
              {replacementCars.map((car) => (
                <option key={car.id} value={car.id}>
                  {car.name}
                  {car.plate ? ` (${car.plate})` : ''}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div style={{ marginTop: 12 }}>
          <label>
            <span style={labelStyle}>Reason for substitution</span>
            <textarea
              rows={3}
              style={textareaStyle}
              value={form.reason}
              onChange={(e) => setForm((prev) => ({ ...prev, reason: e.target.value }))}
              disabled={readOnly || submitted}
              placeholder="Explain why the car had to be replaced"
            />
          </label>
        </div>

        <div style={{ ...twoColGrid, marginTop: 12 }}>
          <label>
            <span style={labelStyle}>Replacement car odometer</span>
            <input
              type="number"
              style={inputStyle}
              value={form.odometer}
              onChange={(e) => setForm((prev) => ({ ...prev, odometer: e.target.value }))}
              disabled={readOnly || submitted}
              placeholder="Optional"
            />
          </label>

          <label>
            <span style={labelStyle}>Replacement car fuel level</span>
            <select
              style={inputStyle}
              value={form.currentFuel}
              onChange={(e) => setForm((prev) => ({ ...prev, currentFuel: e.target.value }))}
              disabled={readOnly || submitted}
            >
              <option value="">Select fuel level</option>
              <option value="1/4">1/4</option>
              <option value="1/2">1/2</option>
              <option value="3/4">3/4</option>
              <option value="full">Full</option>
            </select>
          </label>
        </div>

        <div style={{ marginTop: 12 }}>
          <Button
            type="button"
            onClick={() => checkAvailability()}
            style={{ background: '#fff', border: '1px solid #d1d5db' }}
          >
            Check replacement availability
          </Button>
          {availability.checked && (
            <div
              style={{
                ...helperTextStyle,
                color: availability.ok ? '#065f46' : '#991b1b',
                marginTop: 8,
              }}
            >
              {availability.message}
            </div>
          )}
        </div>
      </section>

      <section style={cardStyle()}>
        <h3 style={sectionTitleStyle}>Replacement car photos</h3>
        <div style={helperTextStyle}>
          These uploads are attached to the substitution email through the same secure media link used
          for pickup and dropoff.
        </div>

        {readOnly && existingHandover ? (
          <div style={{ marginTop: 10 }}>
            <span style={labelStyle}>Odometer / fuel photos</span>
            {renderImageGrid(
              existingOdoFuelUrls,
              'No odometer / fuel photos uploaded for this substitution.'
            )}
          </div>
        ) : (
          <FileUploadWithPreview
            label="Odometer / fuel photos"
            uploads={odoFuelUploads}
            onUploadsChange={setOdoFuelUploads}
            uploadPrefix="handover/substitution/car"
            reservationId={reservationId}
            accept="image/*"
            disabled={readOnly || submitted}
            preserveUploadedOnUnmount={submitted}
          />
        )}

        <div style={{ marginTop: 12 }}>
          {readOnly && existingHandover ? (
            <>
              <span style={labelStyle}>Replacement car body photos</span>
              {renderImageGrid(
                existingBodyPhotoUrls,
                'No body photos uploaded for this substitution.'
              )}
            </>
          ) : (
            <FileUploadWithPreview
              label="Replacement car body photos"
              uploads={bodyPhotoUploads}
              onUploadsChange={setBodyPhotoUploads}
              uploadPrefix="handover/substitution/damage"
              reservationId={reservationId}
              accept="image/*"
              disabled={readOnly || submitted}
              preserveUploadedOnUnmount={submitted}
            />
          )}
        </div>
      </section>

      <section style={cardStyle()}>
        <h3 style={sectionTitleStyle}>Extra charges</h3>
        <div style={helperTextStyle}>
          Record whether the replacement was free, still payable, or already collected.
        </div>

        <div style={{ ...threeColGrid, marginTop: 12 }}>
          <label>
            <span style={labelStyle}>Charge status</span>
            <select
              style={inputStyle}
              value={form.extraChargeStatus}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  extraChargeStatus: e.target.value,
                  extraChargePaymentMethod: e.target.value === 'paid' ? prev.extraChargePaymentMethod : '',
                }))
              }
              disabled={readOnly || submitted}
            >
              <option value="free">Free of charge</option>
              <option value="unpaid">Charge added but not paid yet</option>
              <option value="paid">Charge already paid</option>
            </select>
          </label>

          <label>
            <span style={labelStyle}>Charge amount</span>
            <input
              type="number"
              step="0.01"
              style={{
                ...inputStyle,
                background: chargeStatus === 'free' ? COLORS.gray[50] : '#ffffff',
              }}
              value={form.extraChargeAmount}
              onChange={(e) => setForm((prev) => ({ ...prev, extraChargeAmount: e.target.value }))}
              disabled={readOnly || submitted || chargeStatus === 'free'}
              placeholder={chargeStatus === 'free' ? 'No charge' : '0.00'}
            />
          </label>

          <label>
            <span style={labelStyle}>Currency</span>
            <select
              style={inputStyle}
              value={form.extraChargeCurrency}
              onChange={(e) => setForm((prev) => ({ ...prev, extraChargeCurrency: e.target.value }))}
              disabled={readOnly || submitted || chargeStatus === 'free'}
            >
              {CURRENCY_OPTIONS.map((currency) => (
                <option key={currency} value={currency}>
                  {currency}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div style={{ marginTop: 12 }}>
          <label>
            <span style={labelStyle}>Charge description</span>
            <textarea
              rows={2}
              style={textareaStyle}
              value={form.extraChargeDescription}
              onChange={(e) => setForm((prev) => ({ ...prev, extraChargeDescription: e.target.value }))}
              disabled={readOnly || submitted}
              placeholder="Why there was an extra fee, or note that the substitution was free"
            />
          </label>
        </div>

        {chargeStatus === 'paid' && (
          <div style={{ ...twoColGrid, marginTop: 12 }}>
            <label>
              <span style={labelStyle}>Paid via</span>
              <select
                style={inputStyle}
                value={form.extraChargePaymentMethod}
                onChange={(e) => setForm((prev) => ({ ...prev, extraChargePaymentMethod: e.target.value }))}
                disabled={readOnly || submitted}
              >
                <option value="">Select payment method</option>
                <option value="cash">Cash</option>
                <option value="card">Card</option>
                <option value="bank">Bank transfer</option>
                <option value="other">Other</option>
              </select>
            </label>

            <label>
              <span style={labelStyle}>Payment reference</span>
              <input
                style={inputStyle}
                value={form.extraChargeNote}
                onChange={(e) => setForm((prev) => ({ ...prev, extraChargeNote: e.target.value }))}
                disabled={readOnly || submitted}
                placeholder="Receipt number, card last 4 digits, or note"
              />
            </label>
          </div>
        )}

        {chargeStatus === 'unpaid' && (
          <div style={{ marginTop: 12 }}>
            <label>
              <span style={labelStyle}>Collection note</span>
              <input
                style={inputStyle}
                value={form.extraChargeNote}
                onChange={(e) => setForm((prev) => ({ ...prev, extraChargeNote: e.target.value }))}
                disabled={readOnly || submitted}
                placeholder="Explain how or when this fee should be collected"
              />
            </label>
          </div>
        )}

        {chargeStatus === 'free' && (
          <div
            style={{
              marginTop: 12,
              borderRadius: 10,
              border: `1px solid ${COLORS.successLight}`,
              background: COLORS.successLight,
              color: '#065f46',
              padding: '10px 12px',
              fontSize: 13,
            }}
          >
            This substitution will be recorded as free of charge unless you switch the status.
          </div>
        )}
      </section>

      <section style={cardStyle()}>
        <h3 style={sectionTitleStyle}>Lessee details</h3>
        <div style={twoColGrid}>
          <label>
            <span style={labelStyle}>First name</span>
            <input
              style={inputStyle}
              value={form.lesseeFirstName}
              onChange={(e) => setForm((prev) => ({ ...prev, lesseeFirstName: e.target.value }))}
              disabled={readOnly || submitted}
            />
          </label>

          <label>
            <span style={labelStyle}>Last name</span>
            <input
              style={inputStyle}
              value={form.lesseeLastName}
              onChange={(e) => setForm((prev) => ({ ...prev, lesseeLastName: e.target.value }))}
              disabled={readOnly || submitted}
            />
          </label>
        </div>

        <div style={{ ...twoColGrid, marginTop: 12 }}>
          <label>
            <span style={labelStyle}>Email</span>
            <input
              type="email"
              style={inputStyle}
              value={form.lesseeEmail}
              onChange={(e) => setForm((prev) => ({ ...prev, lesseeEmail: e.target.value }))}
              disabled={readOnly || submitted}
            />
          </label>

          <label>
            <span style={labelStyle}>Confirm email</span>
            <input
              type="email"
              style={inputStyle}
              value={form.lesseeEmailConfirm}
              onChange={(e) => setForm((prev) => ({ ...prev, lesseeEmailConfirm: e.target.value }))}
              disabled={readOnly || submitted}
            />
          </label>
        </div>

        <div style={{ ...twoColGrid, marginTop: 12 }}>
          <label>
            <span style={labelStyle}>Phone</span>
            <input
              style={inputStyle}
              value={form.lesseePhone}
              onChange={(e) => setForm((prev) => ({ ...prev, lesseePhone: e.target.value }))}
              disabled={readOnly || submitted}
            />
          </label>

          <label>
            <span style={labelStyle}>ID / passport</span>
            <input
              style={inputStyle}
              value={form.idNumber}
              onChange={(e) => setForm((prev) => ({ ...prev, idNumber: e.target.value }))}
              disabled={readOnly || submitted}
            />
          </label>
        </div>

        <div style={{ ...twoColGrid, marginTop: 12 }}>
          <label>
            <span style={labelStyle}>Driving licence</span>
            <input
              style={inputStyle}
              value={form.drivingLicenseNumber}
              onChange={(e) => setForm((prev) => ({ ...prev, drivingLicenseNumber: e.target.value }))}
              disabled={readOnly || submitted}
            />
          </label>

          <label>
            <span style={labelStyle}>Licence country</span>
            <input
              style={inputStyle}
              value={form.drivingLicenseCountry}
              onChange={(e) => setForm((prev) => ({ ...prev, drivingLicenseCountry: e.target.value }))}
              disabled={readOnly || submitted}
            />
          </label>
        </div>

        <div style={{ marginTop: 12 }}>
          <label>
            <span style={labelStyle}>Additional notes</span>
            <textarea
              rows={3}
              style={textareaStyle}
              value={form.extraNotes}
              onChange={(e) => setForm((prev) => ({ ...prev, extraNotes: e.target.value }))}
              disabled={readOnly || submitted}
            />
          </label>
        </div>
      </section>

      <section style={cardStyle()}>
        <h3 style={sectionTitleStyle}>Signatures</h3>
        <div style={{ marginBottom: 10 }}>
          <span style={labelStyle}>Lessor (Haste LLC)</span>
          <div style={helperTextStyle}>Haste LLC E-Signature Digital ID 55712233254412</div>
        </div>
        <div>
          <span style={labelStyle}>Lessee signature</span>
          <div style={helperTextStyle}>The customer signs this substitution agreement electronically.</div>
          {readOnly || submitted ? (
            <div
              style={{
                marginTop: 10,
                borderRadius: 8,
                border: `1px dashed ${COLORS.gray[300]}`,
                padding: 12,
                background: COLORS.gray[50],
                color: COLORS.gray[500],
                fontSize: 12,
              }}
            >
              Signature already captured.
            </div>
          ) : (
            <div style={{ marginTop: 10 }}>
              <SignaturePad
                height={180}
                onChange={(dataUrl) => setForm((prev) => ({ ...prev, lesseeSignatureDataUrl: dataUrl }))}
              />
            </div>
          )}
        </div>
      </section>

      <section style={cardStyle()}>
        <h3 style={sectionTitleStyle}>Contract terms</h3>
        <Button
          type="button"
          onClick={() => setShowContract((prev) => !prev)}
          style={{ background: '#fff', border: '1px solid #d1d5db' }}
        >
          {showContract ? 'Hide contract text' : 'Show contract text'}
        </Button>
        {showContract && (
          <pre
            style={{
              marginTop: 12,
              whiteSpace: 'pre-wrap',
              fontFamily: 'inherit',
              fontSize: 12,
              lineHeight: 1.6,
              color: COLORS.gray[700],
              background: COLORS.gray[50],
              border: `1px solid ${COLORS.gray[200]}`,
              borderRadius: 12,
              padding: 16,
            }}
          >
            {contractText}
          </pre>
        )}
      </section>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <Button
          type="submit"
          disabled={readOnly || submitted || submitting || pendingPhotoUploads > 0 || failedPhotoUploads > 0}
        >
          {submitting
            ? 'Saving...'
            : submitted
            ? 'Saved'
            : pendingPhotoUploads > 0
            ? `Uploading photos (${pendingPhotoUploads})...`
            : failedPhotoUploads > 0
            ? 'Resolve failed uploads'
            : 'Save substitution contract'}
        </Button>
      </div>
    </form>
  );
}
